import type { MouseEvent } from "react";
import { useEffect, useMemo, useState } from "react";

import classNames from "classnames";

import { useConfig } from "@jti/app-context/useConfig";
import { LongArrowForwardIcon } from "@jti/brand-icons/long-arrow-forward";
import { Button } from "@jti/core-components/components/Button";
import { Card } from "@jti/core-components/components/Card";
import { Loader } from "@jti/core-components/components/Loader";
import { AUTH_ROUTE_PATHS } from "@jti/fe-common/routes/auth-routes";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import { fetchPointsHistory } from "@jti/store/slices/loyaltyCoreSlice";
import { type RootStoreState } from "@jti/store/types";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";
import { LoadMore } from "@jti/ui/components/LoadMore";
import { useAuthHrefResolver } from "@jti/ui/providers/AuthRoutingProvider";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import useLoggedUser from "../../hooks/useLoggedUser";
import { redirectToAuthPage } from "../../utils/redirectToAuthPage";
import { HistoryTable } from "../HistoryTable/HistoryTable";
import ScrollToTop from "../ScrollToTop/ScrollToTop";

import { historyItemsAuthoring } from "./__data__/historyItemsAuthoring";

import styles from "./PointsHistory.module.scss";

export type PointsHistoryProps = {
  isFullPointsHistory: boolean;
  url: string;
  text: string;
  title: string;
  description?: string;
  loadAmount: number;
  defaultPointsAddedMessage: string;
  defaultPointsSpentMessage: string;
  pointsRefundMessage?: string;
  pointsRedemptionMessage: string;
  pointsCouponRedemptionMessage: string;
  pointsOrderAddedMessage: string;
  pointsCancellationMessage: string;
  pointsExpirationMessage: string;
  externalCouponLinkLabel?: string;
  dateLabel: string;
  eventLabel: string;
  pointsLabel: string;
  balanceLabel: string;
  isInEditor: boolean;
  removeMargin?: boolean;
  isCouponDisplayedInNewLineOnMobile?: boolean;
  couponLabel?: string;
  pinLabel?: string;
  redemptionFailedMessage?: string;
};

export const PointsHistory = ({
  isFullPointsHistory,
  loadAmount = 5,
  text,
  title = "Points history",
  defaultPointsAddedMessage,
  defaultPointsSpentMessage,
  pointsRefundMessage = "Points refunded",
  pointsOrderAddedMessage,
  pointsRedemptionMessage,
  pointsCouponRedemptionMessage,
  pointsCancellationMessage,
  pointsExpirationMessage,
  externalCouponLinkLabel,
  balanceLabel,
  eventLabel,
  pointsLabel,
  dateLabel,
  url,
  isInEditor,
  removeMargin,
  description,
  isCouponDisplayedInNewLineOnMobile = false,
  couponLabel,
  pinLabel,
  redemptionFailedMessage,
}: PointsHistoryProps) => {
  const config = useConfig();
  const resolveAuthHref = useAuthHrefResolver();
  const dispatch = useDispatch();
  const [isLogged] = useLoggedUser();
  const [pageNo, setPageNo] = useState(1);
  const { __ } = useTranslations();
  const { historyPoints: storeHistoryPoints } = useSelector((state: RootStoreState) => state.loyaltyCore);
  const historyPoints = isInEditor ? historyItemsAuthoring : storeHistoryPoints;
  const labels = { points: pointsLabel, event: eventLabel, balance: balanceLabel, date: dateLabel };

  useEffect(() => {
    if (historyPoints.statusCode === 403 && !isInEditor) {
      redirectToAuthPage(
        resolveAuthHref({
          route: AUTH_ROUTE_PATHS.login,
          search: { returnUrl: config["base-url"] },
        })
      );
    }
  }, [historyPoints.statusCode, isInEditor, config, resolveAuthHref]);

  const eventMessages = {
    defaultPointsAddedMessage,
    defaultPointsSpentMessage,
    pointsRefundMessage,
    pointsRedemptionMessage,
    pointsCouponRedemptionMessage,
    pointsOrderAddedMessage,
    pointsCancellationMessage,
    pointsExpirationMessage,
    externalCouponLinkLabel,
    pinLabel,
    couponLabel,
    redemptionFailedMessage,
  };

  const dispatchGTMEvent = (e: MouseEvent<Element, Event>): void => {
    const clickedElement = e.target as HTMLElement;
    const ctaName = clickedElement.textContent;

    dispatchTrackingEvent(TrackingEvents.TrackingPointsHistoryClick, {
      cta_name: ctaName || "pointsHistory",
    });
  };

  const handleShowMoreItems = () => {
    setPageNo(pageNo + 1);
  };

  const loadMoreLeft = useMemo(() => {
    const itemsLeft = historyPoints.total - historyPoints.items.length;
    return itemsLeft < loadAmount ? itemsLeft : loadAmount;
  }, [historyPoints.items.length, historyPoints.total, loadAmount]);

  useEffect(() => {
    dispatch(fetchPointsHistory({ perPage: loadAmount, pageNo: pageNo }));
  }, [pageNo, loadAmount, dispatch]);

  const showLoadMoreButton = historyPoints.isLoaded && loadMoreLeft !== 0;

  if (!isLogged && !isInEditor) {
    return null;
  }

  const content = (
    <div className={styles.container}>
      <HistoryTable
        rows={historyPoints.items}
        labels={labels}
        variant={"points"}
        eventMessages={eventMessages}
        isCouponDisplayedInNewLineOnMobile={isCouponDisplayedInNewLineOnMobile}
      />
      {showLoadMoreButton && (
        <LoadMore className={styles.loadMore} label={__("loyalty.load.more.label")} onClick={handleShowMoreItems} />
      )}
      {!historyPoints.isLoaded && (
        <div className={styles.loaderContainer}>
          <Loader />
        </div>
      )}
      {!isFullPointsHistory && (
        <div className={styles.linkWrapper}>
          <Button size="sm" variant="text-muted" href={url} onClick={dispatchGTMEvent} iconAfter={LongArrowForwardIcon}>
            {text}
          </Button>
        </div>
      )}
      {isFullPointsHistory && <ScrollToTop />}
    </div>
  );

  if (isFullPointsHistory) {
    return content;
  }

  return (
    <div
      className={classNames(
        {
          [styles.noMargin]: removeMargin,
        },
        styles.pointsHistory
      )}
    >
      <Card title={title} description={description} showSeparator>
        {content}
      </Card>
    </div>
  );
};
